import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, X } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

/**
 * Bandeau RGPD — Informatif, non bloquant.
 * Affiché au premier chargement uniquement (persisté en localStorage).
 * Masqué sur les pages admin et en mode natif (Android).
 */
export const PrivacyBanner: React.FC = () => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Ne pas afficher sur mobile natif ni en admin
        if (Capacitor.isNativePlatform()) return;
        if (window.location.pathname.startsWith('/admin')) return;

        const accepted = localStorage.getItem('ssi_privacy_accepted');
        if (!accepted) {
            // Petit délai pour ne pas gêner le chargement initial
            const timer = setTimeout(() => setVisible(true), 1500);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem('ssi_privacy_accepted', 'true');
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <>
            <style>{`@keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
            <div
                className="fixed bottom-0 left-0 right-0 z-50"
                style={{ animation: 'slideUp 0.5s ease-out' }}
            >
                <div className="max-w-4xl mx-auto px-4 pb-4">
                    <div className="bg-[#161b22]/95 backdrop-blur-xl border border-white/10 rounded-xl px-5 py-4 shadow-2xl">
                        <div className="flex items-start gap-3">
                            <Shield className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-white/80 text-sm leading-relaxed">
                                    {t('privacy.banner.text')}{' '}
                                    <Link
                                        to="/confidentialite"
                                        className="text-primary hover:underline whitespace-nowrap"
                                    >
                                        {t('privacy.banner.link')}
                                    </Link>
                                </p>
                            </div>
                            <button
                                onClick={handleAccept}
                                className="shrink-0 px-4 py-1.5 bg-primary/20 hover:bg-primary/30 text-primary text-sm font-medium rounded-lg transition-colors border border-primary/30"
                            >
                                {t('privacy.banner.accept')}
                            </button>
                            <button
                                onClick={handleAccept}
                                className="shrink-0 p-1 text-slate-500 hover:text-white transition-colors"
                                aria-label="Fermer"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

